import { useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import BigNumber from "bignumber.js";
import { Calendar, Trash2 } from "react-feather";
import { Connection, Keypair, PublicKey, Transaction } from "@solana/web3.js";
import ReactJson from "react-json-view";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import { BN } from "bn.js";
import useLanguage from "@axvdex/hooks/useLanguage";
import {
  InitializeSaleArgs,
  createInitializeSaleInstruction,
  errorFromCode,
} from "@axvdex/solana/outbidAPI/js/generated";
import { useAppDispatch } from "@axvdex/state";
import CustomSelect from "@axvdex/components/form-element/CustomSelect";
import { SaleBidAsset, MetaSaleInfo, OutbidAsset, OutbidSale } from "@axvdex/types/solana/outbid";
import Button from "@axvdex/components/common/Button";
import imgSanitize from "@axvdex/utils/imgSanitize";
import { processError } from "@axvdex/hooks/solana/utils/client";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import {
  findSoldOutBidderIncentives,
  findSpecificBidsIncentives,
  findTransferAuthority,
} from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { getSale } from "@axvdex/hooks/solana/useOutbid";
import { f64ToQ6464, q6464ToF64 } from "@axvdex/hooks/solana/utils/convertion";
import { SolanaSaleOperatorControlsProps } from "../SolanaSaleOperatorControls";

type TargetAllocation = {
  vesting_duration: string;
  target_asset_allocation_amount: string;
  decimals: string;
  target_ticket_amount: string;
};

type SaleSettings = {
  min_ticket: string;
  max_ticket: string;
  duration: number;
  whitelist_duration: number;
  bonus_duration: number;
  bonus_min_ticket_increase: string;
  bonus_sell_out_elegible_token_per: number;
  bonus_sell_out_bonus_token_per: number;
  wallet_max_ticket_cap: string;
};

const CreateSale = ({ outbidSale }: SolanaSaleOperatorControlsProps) => {
  const { i18 } = useLanguage();
  const { sendTransaction } = useWallet();
  const { connection } = useConnection();
  const dispatch = useAppDispatch();
  const [createSaleData, setCreateSaleData] = useState<InitializeSaleArgs | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [withDate, setWithDate] = useState<boolean>(false);
  const [bidAssets, setBidAssets] = useState<SaleBidAsset[]>([]);
  const [bidAssetSelection, setBidAssetSelection] = useState(null);
  const [bidAssetSelectionRatio, setBidAssetSelectionRatio] = useState<string>("");
  const [targetAllocation, setTargetAllocation] = useState<TargetAllocation>({
    vesting_duration: "0",
    target_asset_allocation_amount: "1000000",
    decimals: "6",
    target_ticket_amount: "50000",
  });
  const [saleSettings, setSaleSettings] = useState<SaleSettings>({
    min_ticket: BigNumber(outbidSale.defaultSaleSettings.min_ticket)
      .div(Math.pow(10, outbidSale.defaultSaleSettings.target_asset_decimals))
      .toString(10),
    max_ticket: BigNumber(outbidSale.defaultSaleSettings.max_ticket)
      .div(Math.pow(10, outbidSale.defaultSaleSettings.target_asset_decimals))
      .toString(10),
    duration: outbidSale.defaultSaleSettings.duration,
    whitelist_duration: outbidSale.defaultSaleSettings.whitelist_duration,
    bonus_duration: outbidSale.defaultSaleSettings.bonus_duration,
    bonus_min_ticket_increase: BigNumber(outbidSale.defaultSaleSettings.bonus_min_ticket_increase)
      .div(Math.pow(10, outbidSale.defaultSaleSettings.target_asset_decimals))
      .toString(10),
    bonus_sell_out_elegible_token_per: outbidSale.defaultSaleSettings.bonus_sell_out_elegible_token_per,
    bonus_sell_out_bonus_token_per: outbidSale.defaultSaleSettings.bonus_sell_out_bonus_token_per,
    wallet_max_ticket_cap: BigNumber(outbidSale.defaultSaleSettings.wallet_max_ticket_cap)
      .div(Math.pow(10, outbidSale.defaultSaleSettings.target_asset_decimals))
      .toString(10),
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadFromPreRound = async (val: MetaSaleInfo) => {
    // Find the sale account and retreive all info from it
    let saleState: MetaSaleInfo | null = null;
    let saleAccount: string | null = null;
    Object.entries(outbidSale.salesState).forEach(([key, value]) => {
      if (value.id === val.id) {
        // Push the key-value pair into the matching entries array
        saleState = value;
        saleAccount = key;
      }
    });
    if (!saleState || !saleAccount) {
      dispatch(sendToast({ type: "error", info: { msg: "No sale found", toastID: "" + new Date().getTime() } }));
      return;
    }
    const sale = await getSale(connection, new PublicKey(saleAccount));

    // Set the data
    console.log("Asset", sale.config.bidAssets[0].mint.toBase58());
    setBidAssets(
      sale.config.bidAssets.map((asset: SaleBidAsset) => {
        return { ...asset, symbol: outbidSale.assets[asset.mint.toBase58()].symbol };
      })
    );
    setTargetAllocation({
      vesting_duration: sale.config.saleSettings.vestingDuration.toString(),
      target_asset_allocation_amount: BigNumber(sale.config.targetAllocation.targetAssetAllocationAmount)
        .div(Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber()))
        .toString(10),
      decimals: sale.config.targetAllocation.targetAssetDecimals.toString(),
      target_ticket_amount: BigNumber(sale.config.targetAllocation.targetTicketAmount)
        .div(Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber()))
        .toString(10),
    });
    setSaleSettings({
      min_ticket: "100", // hardcode this because prev rounds if they go to bonus this field is changed at contract level
      max_ticket: BigNumber(sale.config.saleSettings.maxTicket)
        .div(Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber()))
        .toString(10),
      duration: sale.config.saleSettings.duration,
      whitelist_duration: sale.config.saleSettings.whitelistDuration,
      bonus_duration: sale.config.saleSettings.bonusDuration,
      bonus_min_ticket_increase: BigNumber(sale.config.saleSettings.bonusMinTicketIncrease)
        .div(Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber()))
        .toString(10),
      bonus_sell_out_elegible_token_per: sale.config.saleSettings.bonusSellOutElegibleTokenPer.toNumber(),
      bonus_sell_out_bonus_token_per: sale.config.saleSettings.bonusSellOutBonusTokenPer.toNumber(),
      wallet_max_ticket_cap: sale.config.saleSettings.walletMaxTicketCap
        ? BigNumber(sale.config.saleSettings.walletMaxTicketCap)
          .div(Math.pow(10, sale.config.targetAllocation.targetAssetDecimals.toNumber()))
          .toString(10)
        : null,
    });
    setDate(null);
  };

  const handleInitializeSale = async () => {
    setIsLoading(true);
    try {
      // Pre-flight checks
      if (createSaleData === null) {
        throw new Error("No data to create sale");
      }
      if (bidAssets.length === 0) {
        throw new Error("Please select at least one bid asset");
      }
      const saleAccount = Keypair.generate();
      const [transferAuthority] = findTransferAuthority(saleAccount.publicKey);
      const [soldOutBidderIncentives] = findSoldOutBidderIncentives(saleAccount.publicKey);
      const [specificBidsIncentives] = findSpecificBidsIncentives(saleAccount.publicKey);

      const bidAssetMint = bidAssets[0].mint;
      const bidAssetAta = getAssociatedTokenAddressSync(bidAssets[0].mint, transferAuthority, true);

      // Build the initialize sale instruction
      const initIx = createInitializeSaleInstruction(
        {
          saleAccount: saleAccount.publicKey,
          saleAuthority: window.solanaClient.publicKey,
          transferAuthority: transferAuthority,
          soldOutBidderIncentives: soldOutBidderIncentives,
          specificBidsIncentives: specificBidsIncentives,
          bidAssetAta: bidAssetAta,
          bidAssetMint: bidAssetMint,
        },
        { initializeSaleArgs: sanitizeInitSaleData(createSaleData) }
      );
      for (let i = 1; i < bidAssets.length; ++i) {
        initIx.keys.push(
          {
            pubkey: getAssociatedTokenAddressSync(bidAssets[i].mint, transferAuthority, true),
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey: bidAssets[i].mint,
            isSigner: false,
            isWritable: false,
          }
        );
      }

      // Build the transaction
      const tx = new Transaction();
      tx.add(initIx);
      tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

      // Send the transaction
      try {
        const signature = await sendTransaction(tx, connection, { signers: [saleAccount] });
        await connection.confirmTransaction(signature, "finalized");
        console.log("Transaction confirmed, sale address:", saleAccount.publicKey.toBase58());
        // INFO: What to do with the sale address?
        // - Contract emit a log with the new sale address
        // - Backend listen to the logs and will push this new sale to all the clients
      } catch (error) {
        console.error(processError(error.message, errorFromCode));
        throw error;
      }
    } catch (error: unknown) {
      console.error(error);
      const msg = error instanceof Error ? error.message : "An error occurred";
      dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "1em" }}>
      <div className="withGradientBorderBottom"></div>
      <div
        className="inlineFlexbox withGradientBorderBottom"
        style={{ width: "100%", marginTop: "1em", paddingBottom: "1em" }}
      >
        Load from prev round:
        <CustomSelect
          labelText={""}
          hiddenLabel={true}
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          /* @ts-ignore */
          items={Object.values(outbidSale.salesState)
            .map((saleState: MetaSaleInfo) => {
              return {
                value: saleState.id,
                label: saleState.id,
                id: saleState.id,
              };
            })
            .filter(asset => asset)}
          placeholder={i18("Select Round", "trade.form.fromToken.select.placeholder")}
          value={null}
          onChange={loadFromPreRound}
          name={""}
        />{" "}
      </div>
      <div
        className="inlineFlexbox withGradientBorderBottom"
        style={{ marginTop: "1em", marginBottom: "1em", width: "100%", paddingBottom: "1em" }}
      >
        <span>Start Date: </span>
        {!withDate && "Now"}
        {!withDate && (
          <Button
            onClick={() => {
              setWithDate(true);
              const date = new Date();
              date.setHours(date.getHours() + 1);
              date.setMinutes(0);
              date.setSeconds(0);
              date.setMilliseconds(0);
              setDate(date.toISOString());
            }}
          >
            <Calendar />
          </Button>
        )}
        {withDate && (
          <>
            <input value={date} onChange={e => setDate(e.target.value)} style={{ maxWidth: "20em" }} />
            <small>
              in {Math.round(((new Date(date).getTime() - new Date().getTime()) / 1000 / 60 / 60) * 100) / 100} hours
            </small>
          </>
        )}
      </div>
      <div
        className="withGradientBorderBottom"
        style={{ marginBottom: "1em", width: "100%", paddingBottom: "1em", textAlign: "left" }}
      >
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Selected Bid Assets: </span>

          <div style={{ marginLeft: ".75em", display: "flex" }}>
            <input
              type="number"
              style={{ maxWidth: "5em", marginRight: "1em" }}
              onChange={e => {
                setBidAssetSelectionRatio(e.target.value);
              }}
              value={bidAssetSelectionRatio}
            />
            <CustomSelect
              labelText={""}
              hiddenLabel={true}
              items={Object.values(outbidSale.assets)
                .map((asset: OutbidAsset) => {
                  return {
                    value: asset.symbol,
                    label: asset.symbol,
                    id: asset.mint.toBase58(),
                    optionPrefix: <img src={imgSanitize(asset.symbol)} alt={""} />,
                  };
                })
                .filter(asset => asset)}
              placeholder={i18("Select token", "trade.form.fromToken.select.placeholder")}
              value={bidAssetSelection}
              onChange={val => setBidAssetSelection(val)}
              name={""}
            />
            <Button
              disabled={!bidAssetSelection || !bidAssetSelectionRatio || "" === bidAssetSelectionRatio}
              onClick={() => {
                setBidAssets([
                  ...bidAssets,
                  {
                    symbol: outbidSale.assets[bidAssetSelection.id].symbol,
                    mint: new PublicKey(bidAssetSelection.id),
                    assetToTicketRatio: BigNumber(f64ToQ6464(BigNumber(bidAssetSelectionRatio).toNumber()).toString()),
                    decimals: outbidSale.assets[bidAssetSelection.id].decimals,
                    price: outbidSale.assets[bidAssetSelection.id].price,
                  },
                ]);
                setBidAssetSelection(null);
                setBidAssetSelectionRatio("");
              }}
            >
              Add
            </Button>
          </div>
        </div>
        {bidAssets.length > 0 && (
          <div style={{ marginTop: "1em" }}>
            {bidAssets.map((asset, index) => {
              return (
                <div
                  key={index}
                  className="inlineFlexbox"
                  style={{ marginRight: "1em", border: "solid", borderColor: "grey", padding: ".5em" }}
                >
                  <Trash2
                    style={{ color: "red", width: "1em" }}
                    onClick={() => {
                      const newBidAssets = [...bidAssets];
                      newBidAssets.splice(index, 1);
                      setBidAssets(newBidAssets);
                    }}
                  />
                  <span>Ratio: {q6464ToF64(asset.assetToTicketRatio).toString()}</span>
                  <img style={{ width: "2em" }} src={imgSanitize(asset.symbol)} alt={""} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div
        className="withGradientBorderBottom"
        style={{ marginTop: "1em", marginBottom: "1em", width: "100%", paddingBottom: "1em", textAlign: "left" }}
      >
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Target Asset allocation amount: </span>
          <input
            style={{ width: "10em" }}
            value={targetAllocation.target_asset_allocation_amount}
            onChange={e => {
              setTargetAllocation({
                ...targetAllocation,
                target_asset_allocation_amount: e.target.value,
              });
            }}
          />
          <span>Decimals: </span>
          <input
            style={{ width: "5em" }}
            value={targetAllocation.decimals}
            onChange={e => {
              setTargetAllocation({
                ...targetAllocation,
                decimals: e.target.value,
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Target ticket amount: </span>
          $
          <input
            style={{ width: "10em" }}
            value={targetAllocation.target_ticket_amount}
            onChange={e => {
              setTargetAllocation({
                ...targetAllocation,
                target_ticket_amount: e.target.value,
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Vesting duration: </span>
          (in seconds)
          <input
            style={{ width: "10em" }}
            value={targetAllocation.vesting_duration}
            onChange={e => {
              setTargetAllocation({
                ...targetAllocation,
                vesting_duration: e.target.value,
              });
            }}
          />
        </div>
      </div>
      <div
        className="withGradientBorderBottom"
        style={{ marginTop: "1em", marginBottom: "1em", width: "100%", paddingBottom: "1em", textAlign: "left" }}
      >
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Minimum per bid: </span>
          $
          <input
            style={{ width: "10em" }}
            value={saleSettings.min_ticket}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                min_ticket: e.target.value,
              });
            }}
          />
          <span>Maximum per bid: </span>
          $
          <input
            style={{ width: "10em" }}
            value={saleSettings.max_ticket}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                max_ticket: e.target.value,
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Durations (in seconds): </span>
          <span>Whitelist: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.whitelist_duration}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                whitelist_duration: parseInt(e.target.value),
              });
            }}
          />
          <span>Bonus: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.bonus_duration}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                bonus_duration: parseInt(e.target.value),
              });
            }}
          />
          <span>Total: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.duration}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                duration: parseInt(e.target.value),
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Bonus ticket increase: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.bonus_min_ticket_increase}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                bonus_min_ticket_increase: e.target.value,
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Bonus sell out elegible per: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.bonus_sell_out_elegible_token_per}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                bonus_sell_out_elegible_token_per: parseInt(e.target.value),
              });
            }}
          />
          <span>Bonus sell out bonus per: </span>
          <input
            style={{ width: "5em" }}
            value={saleSettings.bonus_sell_out_bonus_token_per}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                bonus_sell_out_bonus_token_per: parseInt(e.target.value),
              });
            }}
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <span>Wallet max ticket cap: </span>
          $
          <input
            style={{ width: "5em" }}
            value={saleSettings.wallet_max_ticket_cap}
            onChange={e => {
              setSaleSettings({
                ...saleSettings,
                wallet_max_ticket_cap: e.target.value,
              });
            }}
          />
        </div>
      </div>

      <div
        className="withGradientBorderBottom"
        style={{ marginTop: "1em", marginBottom: "1em", width: "100%", paddingBottom: "1em", textAlign: "left" }}
      >
        <div className="inlineFlexbox" style={{ width: "100%", placeContent: "center" }}>
          <Button
            btnColor="gradient"
            onClick={async () => {
              const id =
                Object.values(outbidSale.salesState)
                  .map(sale => sale.id)
                  .reduce((a, b) => (a > b ? a : b), 0) + 1;
              const factor = Math.pow(10, BigNumber(targetAllocation.decimals.toString()).toNumber());
              const initialezedSaleArg: InitializeSaleArgs = {
                id,
                outbidLabel: outbidSale.name,
                targetAssetAllocationAmount: BigNumber(targetAllocation.target_asset_allocation_amount)
                  .times(Math.pow(10, BigNumber(targetAllocation.decimals).toNumber()))
                  .toNumber(),
                targetAssetDecimals: parseInt(targetAllocation.decimals),
                targetTicketAmount: BigNumber(targetAllocation.target_ticket_amount).times(factor).toNumber(),
                targetAssetMint: await getTargetMintFromLastSale(connection, outbidSale),
                vestingDuration: new BN(targetAllocation.vesting_duration),
                minTicket: BigNumber(saleSettings.min_ticket).times(factor).toNumber(),
                maxTicket: BigNumber(saleSettings.max_ticket).times(factor).toNumber(),
                startTime: withDate ? new Date(date).getTime() : new Date().getTime(),
                duration: saleSettings.duration,
                whitelistDuration: saleSettings.whitelist_duration,
                bonusDuration: saleSettings.bonus_duration,
                bonusMinTicketIncrease: BigNumber(saleSettings.bonus_min_ticket_increase).times(factor).toNumber(),
                bonusSellOutEligibleTokenPer: saleSettings.bonus_sell_out_elegible_token_per,
                bonusSellOutBonusTokenPer: saleSettings.bonus_sell_out_bonus_token_per,
                walletMaxTicketCap: BigNumber(saleSettings.wallet_max_ticket_cap).times(factor).toNumber(),
                bidAssets: bidAssets.map(asset => {
                  return {
                    mint: asset.mint,
                    decimals: asset.decimals,
                    assetToTicketRatio: asset.assetToTicketRatio.toNumber(),
                  };
                }),
              };
              setCreateSaleData(initialezedSaleArg);
            }}
          >
            Update JSON
          </Button>
        </div>
      </div>

      {createSaleData && (
        <>
          <ReactJson
            style={{ width: "100%", textAlign: "left" }}
            src={createSaleData}
            theme={"threezerotwofour"}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore: Schema sanitized later (before sending it)
            onEdit={a => setCreateSaleData(a.updated_src)}
          />
          <br />
          <Button btnColor="gradient" onClick={handleInitializeSale} disabled={isLoading}>
            {isLoading ? "Executing ..." : "Create"}
          </Button>
        </>
      )}
    </div>
  );
};

export default CreateSale;

/// HELPERS ============================================================================================================

function sanitizeInitSaleData(createSaleData: InitializeSaleArgs | null): InitializeSaleArgs {
  if (createSaleData === null) {
    throw new Error("No data to create sale");
  }

  // Verify all the assets are unique
  const seen = new Set();
  for (const asset of createSaleData.bidAssets) {
    if (seen.has(asset.mint.toBase58())) {
      throw new Error("Duplicate asset in bid assets");
    }
    seen.add(asset.mint.toBase58());
  }

  // Convert all input to a BN
  return {
    id: new BN(createSaleData.id),
    outbidLabel: "Astrovault-Solana",
    targetAssetAllocationAmount: new BN(createSaleData.targetAssetAllocationAmount),
    targetAssetDecimals: createSaleData.targetAssetDecimals,
    targetTicketAmount: new BN(createSaleData.targetTicketAmount),
    targetAssetMint: createSaleData.targetAssetMint,
    vestingDuration: new BN(createSaleData.vestingDuration),
    minTicket: new BN(createSaleData.minTicket),
    maxTicket: new BN(createSaleData.maxTicket),
    startTime: new BN(createSaleData.startTime).div(new BN(1000)), // JS timestamps are in ms & Rust timestamps are in s
    duration: createSaleData.duration,
    whitelistDuration: createSaleData.whitelistDuration,
    bonusDuration: createSaleData.bonusDuration,
    bonusMinTicketIncrease: new BN(createSaleData.bonusMinTicketIncrease),
    bonusSellOutEligibleTokenPer: createSaleData.bonusSellOutEligibleTokenPer,
    bonusSellOutBonusTokenPer: createSaleData.bonusSellOutBonusTokenPer,
    walletMaxTicketCap: createSaleData.walletMaxTicketCap ? new BN(createSaleData.walletMaxTicketCap) : null,
    bidAssets: createSaleData.bidAssets.map(asset => {
      return {
        mint: new PublicKey(asset.mint),
        decimals: asset.decimals,
        assetToTicketRatio: new BN(asset.assetToTicketRatio.toString()),
      };
    }),
  };
}

async function getTargetMintFromLastSale(connection: Connection, outbidSale: OutbidSale): Promise<PublicKey | null> {
  // find the biggest sale id
  let lastSaleId: number | null = null;
  let saleAccount: string | null = null;
  Object.entries(outbidSale.salesState).map(([key, value]) => {
    if (lastSaleId === null) {
      lastSaleId = value.id;
      saleAccount = key;
    } else if (value.id > lastSaleId) {
      lastSaleId = value.id;
      saleAccount = key;
    }
  });
  if (lastSaleId === null || saleAccount === null) return null;
  const sale = await getSale(connection, new PublicKey(saleAccount));
  return sale.config.targetAllocation.targetAssetMint;
}
